import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api'
import { IMPAIRMENT_ITEMS, ITEMS, SYMPTOM_ITEMS, type ItemId, type Severity } from '../drsp'
import { todayIso } from '../dates'
import type { Bleeding, Entry, EntryPatch } from '../types'
import DateNav, { type SaveState } from '../components/DateNav'
import RatingRow from '../components/RatingRow'

const EMPTY = (date: string): Entry => ({
  date,
  bleeding: null,
  items: {},
  note: null,
  updated_at: 0,
})

export default function Today() {
  const [date, setDate] = useState(todayIso)
  const [entry, setEntry] = useState<Entry | null>(null)
  const [save, setSave] = useState<SaveState>('idle')
  const seqRef = useRef(0)

  useEffect(() => {
    let cancelled = false
    setEntry(null)
    api
      .entry(date)
      .then((e) => {
        if (!cancelled) setEntry(e ?? EMPTY(date))
      })
      .catch(() => {
        if (!cancelled) setEntry(EMPTY(date))
      })
    return () => {
      cancelled = true
    }
  }, [date])

  /**
   * Ratings write straight through — a tap is a deliberate act and losing one
   * to a closed tab would be worse than an extra request. The note is debounced
   * separately below.
   */
  const push = useCallback(
    async (patch: EntryPatch) => {
      setSave('saving')
      const seq = ++seqRef.current
      try {
        await api.patch(date, patch)
        // The optimistic state set by the caller is authoritative: a rapid
        // sequence of taps can have responses land out of order, and writing
        // the response body back would briefly revert a later tap.
        if (seq === seqRef.current) setSave('saved')
      } catch {
        if (seq === seqRef.current) setSave('error')
      }
    },
    [date],
  )

  function setItem(id: ItemId, value: Severity | null) {
    setEntry((prev) => {
      if (!prev) return prev
      const items = { ...prev.items }
      if (value === null) delete items[id]
      else items[id] = value
      return { ...prev, items }
    })
    void push({ items: { [id]: value } })
  }

  function setBleeding(value: Bleeding | null) {
    setEntry((prev) => (prev ? { ...prev, bleeding: value } : prev))
    void push({ bleeding: value })
  }

  // The note is typed, not tapped, so it saves on a trailing debounce.
  const noteTimer = useRef<number | undefined>(undefined)
  function setNote(value: string) {
    setEntry((prev) => (prev ? { ...prev, note: value } : prev))
    window.clearTimeout(noteTimer.current)
    noteTimer.current = window.setTimeout(() => void push({ note: value || null }), 600)
  }
  useEffect(() => () => window.clearTimeout(noteTimer.current), [])

  if (!entry) return <div className="empty-state">Loading…</div>

  const answered = ITEMS.filter((i) => entry.items[i.id] !== undefined).length

  return (
    <>
      <DateNav
        date={date}
        onChange={setDate}
        answered={answered}
        total={ITEMS.length}
        save={save}
      />

      <div className="card bleeding">
        <span className="label">Bleeding</span>
        {(
          [
            [null, 'None'],
            ['S', 'Spotting'],
            ['M', 'Menses'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={label}
            type="button"
            aria-pressed={entry.bleeding === value}
            onClick={() => setBleeding(value)}
          >
            {label}
          </button>
        ))}
      </div>

      <p className="section-head">Symptoms</p>
      <div className="card" style={{ padding: 0 }}>
        {SYMPTOM_ITEMS.map((item) => (
          <RatingRow
            key={item.id}
            item={item}
            value={entry.items[item.id]}
            onChange={(v) => setItem(item.id, v)}
          />
        ))}
      </div>

      <p className="section-head">Impact on daily life</p>
      <div className="card" style={{ padding: 0 }}>
        {IMPAIRMENT_ITEMS.map((item) => (
          <RatingRow
            key={item.id}
            item={item}
            value={entry.items[item.id]}
            onChange={(v) => setItem(item.id, v)}
          />
        ))}
      </div>

      <p className="section-head">Note</p>
      <div className="card">
        <textarea
          value={entry.note ?? ''}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Anything worth remembering about today (optional)"
        />
      </div>

      {save === 'error' && (
        <div className="save-cue show error" role="alert">
          ⚠ Not saved — check your connection
        </div>
      )}
    </>
  )
}
